import { useEffect, useRef } from 'react';
import { Tag } from 'antd';
import i18n from 'i18next';
import type { ParseKeys, TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '@/i18n/format';
import { useWebchatStore } from '@/store/webchat.store';
import styles from './translation-log-panel.module.scss';

type LogTagConfig = { labelKey: ParseKeys<'webchat'>; color: string };

const STEP_CONFIG: Record<string, LogTagConfig> = {
  transcribing: { labelKey: 'translationLog.steps.transcribing', color: 'blue' },
  translating:  { labelKey: 'translationLog.steps.translating',  color: 'orange' },
  speaking:     { labelKey: 'translationLog.steps.speaking',     color: 'green' },
};

const ROLE_CONFIG: Record<string, LogTagConfig> = {
  visitor: { labelKey: 'translationLog.roles.visitor', color: 'purple' },
  agent:   { labelKey: 'translationLog.roles.agent',   color: 'cyan' },
};

function formatTime(ms: number): string {
  return new Date(ms).toLocaleTimeString(i18n.language, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatDuration(ms: number | null, t: TFunction<'webchat'>): string {
  if (ms === null) return '…';
  if (ms < 1000) return t('translationLog.duration.milliseconds', { value: formatNumber(ms) });
  return t('translationLog.duration.seconds', {
    value: formatNumber(ms / 1000, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
  });
}

interface TranslationLogPanelProps {
  conversationId: number;
}

export const TranslationLogPanel = ({ conversationId }: TranslationLogPanelProps) => {
  const { t } = useTranslation('webchat');
  const entries = useWebchatStore((s) => s.bridgeLogs[conversationId] ?? []);
  const endRef = useRef<HTMLDivElement>(null);
  const prevLengthRef = useRef(0);

  useEffect(() => {
    if (entries.length !== prevLengthRef.current) {
      prevLengthRef.current = entries.length;
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [entries.length]);

  if (entries.length === 0) {
    return (
      <div className={styles.empty}>
        {t('translationLog.emptyTitle')}
        <br />
        {t('translationLog.emptyHint')}
      </div>
    );
  }

  return (
    <div className={styles.log}>
      {entries.map((entry) => {
        const stepCfg = STEP_CONFIG[entry.status];
        const roleCfg = ROLE_CONFIG[entry.role];
        const isActive = entry.durationMs === null;

        return (
          <div
            key={entry.id}
            className={`${styles.entry} ${isActive ? styles.active : ''}`}
          >
            <span className={styles.time}>{formatTime(entry.startedAt)}</span>
            <Tag color={roleCfg?.color ?? 'default'} className={styles.roleTag}>
              {roleCfg ? t(roleCfg.labelKey) : entry.role}
            </Tag>
            <Tag color={stepCfg?.color ?? 'default'} className={styles.stepTag}>
              {stepCfg ? t(stepCfg.labelKey) : entry.status}
            </Tag>
            <span className={`${styles.duration} ${isActive ? styles.durationActive : ''}`}>
              {formatDuration(entry.durationMs, t)}
            </span>
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  );
};
